<template>
  <select
    @change="(e) => $emit('update:player', e.target.value)"
    class="tournament-pairing-player-select"
  >
    <option>Select a player</option>
    <option
      v-for="player in availableUnpairedPlayers"
      :key="boardNumber + color + player.username"
      :value="player.username"
    >
      {{ formatPlayerOption(player) }}
    </option>
  </select>
</template>

<script>
export default {
  name: "TournamentPairingPlayerSelect",

  props: {
    availableUnpairedPlayers: {
      type: Array,
      required: true,
    },
    boardNumber: {
      type: Number,
      required: true,
    },
    color: {
      type: String,
      required: true,
      validator: (value) => ["white", "black"].includes(value),
    },
    opponentPlayer: {
      type: Object,
      default: null,
    },
    games: {
      type: Array,
      required: true,
    },
    roundNumber: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      resultCache: new Map(), // Cache result calculations
    };
  },

  methods: {
    hasPlayed(username1, username2) {
      if (!username1 || !username2) return false;

      const key = `${username1}-${username2}`;
      if (this.resultCache.has(key)) {
        return this.resultCache.get(key);
      }

      // Find the first player in availableUnpairedPlayers to get their game history
      const player1 = this.availableUnpairedPlayers.find(
        (p) => p.username === username1
      );
      if (!player1 || !player1.games) {
        this.resultCache.set(key, false);
        return false;
      }

      // Check if player1 has played against username2 in any previous round
      const result = player1.games.some(
        (g) =>
          g.roundNumber < this.roundNumber &&
          ((g.blackUsername === username1 && g.whiteUsername === username2) ||
            (g.blackUsername === username2 && g.whiteUsername === username1))
      );

      this.resultCache.set(key, result);
      return result;
    },

    /**
     * Check if there's a color preference conflict between player and opponent
     */
    hasColorPreferenceConflict(player) {
      if (!this.opponentPlayer) {
        return false;
      }

      // Extract numeric color preference from colorPref string
      // colorPref format is like "[wb]+1" or "[b]-2"
      const playerColorPref = this.extractColorPrefNumber(player.colorPref);
      const opponentColorPref = this.extractColorPrefNumber(
        this.opponentPlayer.colorPref
      );

      // Check if both players prefer the same color
      // Positive colorPref means prefers white, negative means prefers black
      return (
        (playerColorPref > 0 && opponentColorPref > 0) ||
        (playerColorPref < 0 && opponentColorPref < 0)
      );
    },

    /**
     * Extract numeric color preference from colorPref string
     * e.g., "[wb]+1" -> 1, "[b]-2" -> -2
     */
    extractColorPrefNumber(colorPref) {
      if (!colorPref) return 0;
      const match = colorPref.match(/([+-]?\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    },

    /**
     * Check if player has had a bye in previous rounds
     */
    hasRepeatBye(player) {
      if (!player.games) {
        return false;
      }

      // Check if this player has had a bye in any previous round
      return player.games.some(
        (g) =>
          g.roundNumber < this.roundNumber &&
          ((!g.whiteUsername && g.blackUsername === player.username) ||
            (!g.blackUsername && g.whiteUsername === player.username))
      );
    },

    /**
     * Format player option text with indicators
     */
    formatPlayerOption(player) {
      let text = `${player.username} ${player.scoreBeforeGame} ${player.colorPref}`;

      const warnings = [];

      if (
        this.opponentPlayer &&
        this.hasPlayed(player.username, this.opponentPlayer.username)
      ) {
        warnings.push("⿻ PLAYED");
      }

      if (this.hasColorPreferenceConflict(player)) {
        warnings.push("🎨 COLOR");
      }

      if (this.hasRepeatBye(player)) {
        warnings.push("ദ BYE"); // Repeat bye warning
      }

      if (warnings.length > 0) {
        text += ` - ${warnings.join(" ")}`;
      }

      return text;
    },
  },

  beforeDestroy() {
    this.resultCache.clear();
  },
};
</script>

<style scoped>
.tournament-pairing-player-select {
  margin: 0.5rem;
}
</style>
